import { useState, useEffect, useMemo } from "react";
import { Box, Flex } from "rebass/styled-components";
import styled from "styled-components";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { InfoSection } from "../InfoSection";
import { Tabs } from "MVVM/Components/Tabs";
import { Divider } from "MVVM/Components/Divider";
import { ShareSection } from "../ShareSection";
import { CampaignContentCallout } from "MVVM/Views/Competitions/Components/CampaignContentCallout";
import { ClipPagePromotion } from "MVVM/Views/ClipExperience/ClipPagePromotion";
import { IClip, TClip } from "MVVM/@types";
import { ClipComments } from "MVVM/Components/Comments/Clip";
import { ClipCommentsViewModel } from "MVVM/Components/Comments/Clip/ViewModel";
import { Feed } from "../Feed";
import { CreatorCards } from "../CreatorCards";
import { FollowList } from "../FollowList";
import { getQueryParam } from "MVVM/Utilities/QueryParam";
import { PromoSession } from "MVVM/State/promoSession";
import { ClipExperienceSidebarSelectedTab } from "MVVM/State/sidebar";
import { ClipContext, IClipPagePromotion } from "../@types";
import { ExpiringPill } from "MVVM/Components/ProfileClips/Components/ExpiringPill";

interface ISidebarProps {
  clip: IClip;
  referrer: string | undefined;
  context: ClipContext | null;
  feedData?: {
    clipsRecommended: TClip[];
  };
  feedError?: Error | null;
  feedLoading?: boolean;
  onRemixClicked: () => void;
  showExpirationBanner?: boolean;
  isOwner?: boolean;
}
const Sidebar = ({
  clip,
  context,
  feedData,
  feedError,
  feedLoading,
  onRemixClicked,
  showExpirationBanner,
  isOwner,
}: ISidebarProps) => {
  const clipInCampaign = !!clip?.campaign?.slug;
  const commentsDisabled = clip.user?.commentsDisabled;
  const { data: commentsData } = ClipCommentsViewModel({ clipId: clip.id });
  const commentCount = commentsData?.clip?.comments?.pageInfo?.totalCount || 0;
  const selectedTab = getQueryParam("tab") || context?.metadata?.tab;
  const [activeTab, setActiveTab] = useState<number>(0);
  const setSelectedTabName = useSetRecoilState(
    ClipExperienceSidebarSelectedTab,
  );
  const promoSession = useRecoilValue(PromoSession);
  const clipPagePromotions = promoSession?.activeClipPagePromos;
  const queryParamPromotion = getQueryParam("promotion");

  const promotionToShow = queryParamPromotion
    ? clipPagePromotions.find(
        (promo: IClipPagePromotion) => promo.name === queryParamPromotion,
      )
    : null;

  const tabContent = useMemo(() => {
    const tabs = {
      comments: {
        component: (
          <ClipComments clipId={clip.id} disabled={commentsDisabled} />
        ),
        name: "comments",
        title: `Comments ${
          !commentsDisabled && commentCount && commentCount > 0
            ? `(${commentCount})`
            : ""
        }`,
        type: "Comments",
      },
      creatorCards: {
        component: <CreatorCards effects={clip.effects} />,
        name: "creator-cards",
        title: "Creator Cards",
        type: "Creator Cards",
      },
      forYouFeed: {
        component: (
          <Feed
            feedData={feedData}
            feedError={feedError}
            feedLoading={feedLoading}
          />
        ),
        name: "recommended",
        title: "Recommended",
        type: "Recommended",
      },
      followList: {
        component: (
          <FollowList
            players={clip?.moment?.match?.players}
            matchId={clip?.moment?.match?.id}
          />
        ),
        name: "in-this-match",
        title: "In This Match",
        type: "In This Match",
      },
    };

    switch (context?.type) {
      case "match":
        return [tabs.comments, tabs.followList, tabs.creatorCards];
      default:
        return [tabs.forYouFeed, tabs.comments, tabs.followList];
    }
  }, [
    clip.id,
    clip.effects,
    clip?.moment?.match?.players,
    clip?.moment?.match?.id,
    commentsDisabled,
    commentCount,
    feedData,
    feedError,
    feedLoading,
    context?.type,
  ]);

  const defaultTab = tabContent.findIndex((tab) => tab.name === selectedTab);

  useEffect(() => {
    if (defaultTab !== -1) {
      setActiveTab(defaultTab);
      const tabName = tabContent[defaultTab]?.name || "recommended";
      setSelectedTabName(tabName);
    } else {
      const firstTabName = tabContent[0]?.name || "recommended";
      setSelectedTabName(firstTabName);
    }
  }, [defaultTab, tabContent, setSelectedTabName]);

  const handleTabChange = (newActiveTab: number) => {
    setActiveTab(newActiveTab);
    const newTabName = tabContent[newActiveTab]?.name || "recommended";
    setSelectedTabName(newTabName);
  };

  return (
    <SidebarContainer>
      {showExpirationBanner && (
        <ExpiringPill
          clipInternalId={clip.internalId}
          clipCreatedAt={clip.createdAt}
          clipTitle={clip.title}
          clipThumbnail={clip.thumbnailStandardUrl}
          isOwner={!!isOwner}
          variant="sidebar"
        />
      )}
      <InfoSection clip={clip} />
      {clipInCampaign && (
        <CampaignContentCallout
          compact={true}
          marketingContent={clip?.campaign?.marketing}
          slug={clip?.campaign?.slug}
          endsAt={clip?.campaign?.endsAt}
          primaryColor={clip?.campaign?.primaryColor}
          secondaryColor={clip?.campaign?.secondaryColor}
        />
      )}
      {promotionToShow && <ClipPagePromotion promotion={promotionToShow} />}

      <Divider />
      <ShareSection clip={clip} onRemixClicked={onRemixClicked} />
      <Divider />
      <Tabs
        tabs={tabContent}
        active={activeTab}
        onTabChange={handleTabChange}
      />
      <ColumnContainer>{tabContent[activeTab]?.component}</ColumnContainer>
    </SidebarContainer>
  );
};

const SidebarContainer = styled(Flex)`
  background: ${({ theme }) => theme.colors.darkerNed};
  flex-direction: column;
  scrollbar-width: none;
  width: 441px;
  z-index: 2;

  @media (max-width: ${(props) => props.theme.breaks.theater}) {
    flex-grow: 1;
    overflow: scroll;
    width: 100%;
  }
`;

const ColumnContainer = styled(Box)`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  width: 100%;
`;

export { Sidebar };
